/**
 * 🔄 SERVICIO DE ENVASES RESTAURADO
 * 
 * Este servicio restaura la funcionalidad crítica eliminada de EnvasesServiceV2
 * manteniendo compatibilidad con el sistema actual de triggers de Firebase.
 * 
 * Funcionalidades principales:
 * - Calcular saldos de envases de clientes
 * - Obtener envases pendientes de devolución
 * - Mostrar correctamente las deudas de envases
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

// ============================================
// TIPOS E INTERFACES
// ============================================

export interface ClienteEnvase {
  id: string;
  cliente_id: string;
  envase_tipo_id: string;
  tipo: 'retornable' | 'comodato';
  entregados: number;
  devueltos: number;
  saldo: number; // Positivo = cliente debe, Negativo = cliente tiene crédito
  ultimaActualizacion: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface TipoEnvase {
  id: string;
  nombre: string;
  tipo: 'retornable' | 'comodato' | 'descartable';
  capacidad_litros: number;
  descripcion?: string;
  material?: 'plastico' | 'vidrio' | 'metal';
  precioDeposito?: number;
  activo: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SaldoEnvasesCliente {
  comodato: Array<{
    envaseTipoId: string;
    envaseTipoNombre: string;
    asignados: number;
    saldo: number;
  }>;
  retornables: Array<{
    envaseTipoId: string;
    envaseTipoNombre: string;
    saldo: number;
  }>;
  totalDeuda: number;
}

// ============================================
// SERVICIO PRINCIPAL
// ============================================

export class EnvasesService {
  private static saldoCache: Map<string, {
    timestamp: number;
    data: SaldoEnvasesCliente;
  }> = new Map();

  private static readonly CACHE_TTL = 2000; // 2 segundos de TTL

  /**
   * Obtener la ruta del tenant del usuario actual
   */
  private static getTenantPath(collectionName: string): string {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('Usuario no autenticado o sin email');
    }
    return `tenants/${user.email}/${collectionName}`;
  }

  /**
   * Convertir timestamps de Firestore a Date
   */
  private static convertTimestamps<T extends Record<string, unknown>>(data: unknown): T {
    const converted = { ...(data as Record<string, unknown>) };
    Object.keys(converted).forEach(key => {
      if (converted[key] instanceof Timestamp) {
        converted[key] = (converted[key] as Timestamp).toDate();
      }
    });
    return converted as T;
  }

  // ============================================
  // GESTIÓN DE TIPOS DE ENVASES
  // ============================================

  /**
   * Obtener todos los tipos de envases activos
   */
  static async getTiposEnvases(): Promise<TipoEnvase[]> {
    try {
      const tiposRef = collection(db, this.getTenantPath('tiposEnvases'));
      const q = query(
        tiposRef,
        where('activo', '==', true)
      );

      const snapshot = await getDocs(q);
      const tipos = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...this.convertTimestamps<Omit<TipoEnvase, 'id'>>(data)
        };
      });

      // Ordenar por nombre en el cliente
      return tipos.sort((a, b) => a.nombre.localeCompare(b.nombre));
    } catch (error) {
      console.error('❌ Error obteniendo tipos de envases:', error);
      return [];
    }
  }

  /**
   * Obtener tipos de envases por tipo específico
   */
  static async getTiposEnvasesPorTipo(tipo: 'comodato' | 'retornable' | 'descartable'): Promise<TipoEnvase[]> {
    try {
      const tiposRef = collection(db, this.getTenantPath('tiposEnvases'));
      const q = query(
        tiposRef,
        where('tipo', '==', tipo),
        where('activo', '==', true)
      );

      const snapshot = await getDocs(q);
      const tipos = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...this.convertTimestamps<Omit<TipoEnvase, 'id'>>(data)
        };
      });
      
      return tipos.sort((a, b) => a.nombre.localeCompare(b.nombre));
    } catch (error) {
      console.error(`❌ Error obteniendo tipos de envases ${tipo}:`, error);
      return [];
    }
  }

  // ============================================
  // GESTIÓN DE ENVASES DE CLIENTES
  // ============================================

  /**
   * Obtener envases de un cliente específico
   */
  static async getEnvasesCliente(clienteId: string): Promise<ClienteEnvase[]> {
    try {
      const envasesRef = collection(db, this.getTenantPath('clienteEnvases'));
      const q = query(
        envasesRef,
        where('cliente_id', '==', clienteId)
      );

      const snapshot = await getDocs(q);
      
      const result = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...this.convertTimestamps<Omit<ClienteEnvase, 'id'>>(data)
        };
      });
      
      return result;
    } catch (error) {
      console.error('❌ Error obteniendo envases del cliente:', error);
      return [];
    }
  }

  /**
   * Obtener envase de un cliente por tipo específico
   */
  static async getEnvaseClientePorTipo(clienteId: string, envaseTipoId: string): Promise<ClienteEnvase | null> {
    try {
      const envasesRef = collection(db, this.getTenantPath('clienteEnvases'));
      const q = query(
        envasesRef,
        where('cliente_id', '==', clienteId),
        where('envase_tipo_id', '==', envaseTipoId)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...this.convertTimestamps<Omit<ClienteEnvase, 'id'>>(data)
      };
    } catch (error) {
      console.error('❌ Error obteniendo envase del cliente por tipo:', error);
      return null;
    }
  }

  // ============================================
  // CÁLCULO DE SALDOS
  // ============================================


  /**
   * Calcular saldo de envases de un cliente
   * Restaura la funcionalidad crítica eliminada de EnvasesServiceV2
   */
  static async calcularSaldoEnvasesCliente(clienteId: string): Promise<SaldoEnvasesCliente> {
    try {
      // Verificar caché
      const now = Date.now();
      const cached = this.saldoCache.get(clienteId);
      if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
        return cached.data;
      }


      // Obtener envases del cliente desde la colección clienteEnvases (actualizada por triggers)
      const envasesCliente = await this.getEnvasesCliente(clienteId);
      
      // Si no hay envases, intentar con sistema legacy
      if (envasesCliente.length === 0) {
        return await this.calcularSaldoEnvasesClienteLegacy(clienteId);
      }
      
      // Obtener tipos de envases para nombres
      const tiposEnvases = await this.getTiposEnvases();
      
      const retornables: Array<{
        envaseTipoId: string;
        envaseTipoNombre: string;
        saldo: number;
      }> = [];
      
      const comodato: Array<{
        envaseTipoId: string;
        envaseTipoNombre: string;
        asignados: number;
        saldo: number;
      }> = [];
      
      let totalDeuda = 0;

      envasesCliente.forEach(envase => {
        const tipoEnvase = tiposEnvases.find(t => t.id === envase.envase_tipo_id);
        
        if (!tipoEnvase) {
          console.warn('⚠️ No se encontró tipo de envase para:', envase.envase_tipo_id);
          return;
        }

        const saldoEnvase = envase.saldo || 0;

        if (envase.tipo === 'retornable') {
          retornables.push({
            envaseTipoId: envase.envase_tipo_id,
            envaseTipoNombre: tipoEnvase.nombre,
            saldo: saldoEnvase
          });
        } else if (envase.tipo === 'comodato') {
          comodato.push({
            envaseTipoId: envase.envase_tipo_id,
            envaseTipoNombre: tipoEnvase.nombre,
            asignados: envase.entregados || 0,
            saldo: saldoEnvase
          });
        }

        // Saldo positivo = cliente debe envases
        if (saldoEnvase > 0) {
          totalDeuda += saldoEnvase;
        }
      });

      const resultado: SaldoEnvasesCliente = { 
        comodato, 
        retornables, 
        totalDeuda 
      };
      
      
      // Guardar en caché
      this.saldoCache.set(clienteId, {
        timestamp: Date.now(),
        data: resultado
      });
      
      return resultado;
    } catch (error) {
      console.error('❌ Error calculando saldo de envases:', error);
      return { comodato: [], retornables: [], totalDeuda: 0 };
    }
  }

  /**
   * Calcular saldo usando sistema legacy (balanceEnvases)
   * Mantiene compatibilidad con entregas antiguas
   */
  static async calcularSaldoEnvasesClienteLegacy(clienteId: string): Promise<SaldoEnvasesCliente> {
    try {
      // Obtener balance del sistema legacy
      const balanceRef = doc(db, this.getTenantPath('balanceEnvases'), clienteId);
      const balanceDoc = await getDoc(balanceRef);
      
      if (!balanceDoc.exists()) {
        return { comodato: [], retornables: [], totalDeuda: 0 };
      }
      
      const balanceData = balanceDoc.data();
      const balanceActual = balanceData?.balanceActual || 0;
      const totalDeuda = balanceActual > 0 ? balanceActual : 0; // Balance positivo = deuda
      
      const retornables: Array<{
        envaseTipoId: string;
        envaseTipoNombre: string;
        saldo: number;
      }> = [];
      
      if (totalDeuda > 0) {
        // Buscar un tipo de envase retornable para mostrar la deuda
        const tiposEnvases = await this.getTiposEnvases();
        const tipoRetornable = tiposEnvases.find(t => t.tipo === 'retornable');
        
        if (tipoRetornable) {
          retornables.push({
            envaseTipoId: tipoRetornable.id,
            envaseTipoNombre: tipoRetornable.nombre,
            saldo: totalDeuda
          });
        }
      }
      
      return { comodato: [], retornables, totalDeuda };
      
    } catch (error) {
      console.error('❌ Error calculando saldo legacy:', error);
      return { comodato: [], retornables: [], totalDeuda: 0 };
    }
  }

  // ============================================
  // UTILIDADES Y COMPATIBILIDAD
  // ============================================

  /**
   * Obtener resumen de envases de un cliente
   */
  static async getResumenEnvasesCliente(clienteId: string): Promise<{
    resumen: Array<{
      envaseTipoId: string;
      envaseTipoNombre: string;
      tipo: string;
      saldo: number;
      deuda: number;
    }>;
    totalDeuda: number;
  }> {
    try {
      const saldoEnvases = await this.calcularSaldoEnvasesCliente(clienteId);
      const resumen: Array<{
        envaseTipoId: string;
        envaseTipoNombre: string;
        tipo: string;
        saldo: number;
        deuda: number;
      }> = [];

      // Procesar retornables
      saldoEnvases.retornables.forEach(envase => {
        const deuda = envase.saldo > 0 ? envase.saldo : 0;
        resumen.push({
          envaseTipoId: envase.envaseTipoId,
          envaseTipoNombre: envase.envaseTipoNombre,
          tipo: 'retornable',
          saldo: envase.saldo,
          deuda
        });
      });

      // Procesar comodato
      saldoEnvases.comodato.forEach(envase => {
        const deuda = envase.saldo > 0 ? envase.saldo : 0;
        resumen.push({
          envaseTipoId: envase.envaseTipoId,
          envaseTipoNombre: envase.envaseTipoNombre,
          tipo: 'comodato',
          saldo: envase.saldo,
          deuda
        });
      });

      return { resumen, totalDeuda: saldoEnvases.totalDeuda };
    } catch (error) {
      console.error('❌ Error obteniendo resumen de envases:', error);
      return { resumen: [], totalDeuda: 0 };
    }
  }

  /**
   * Verificar si existen tipos de envases
   */
  static async verificarTiposEnvasesExistentes(): Promise<boolean> {
    try {
      const tipos = await this.getTiposEnvases();
      return tipos.length > 0;
    } catch (error) {
      console.error('❌ Error verificando tipos de envases:', error);
      return false;
    }
  }

  /**
   * Obtener clientes con saldo positivo (deuda de envases)
   */
  static async getClientesConSaldoPositivo(): Promise<ClienteEnvase[]> {
    try {
      const envasesRef = collection(db, this.getTenantPath('clienteEnvases'));
      const q = query(
        envasesRef,
        where('saldo', '>', 0),
        orderBy('saldo', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...this.convertTimestamps<Omit<ClienteEnvase, 'id'>>(data)
        };
      });
    } catch (error) {
      console.error('❌ Error obteniendo clientes con saldo positivo:', error);
      return [];
    }
  }

  /**
   * Limpiar caché de saldos
   */
  static limpiarCache(): void {
    this.saldoCache.clear();
  }
}

export default EnvasesService;
